import React, { useEffect, useMemo, useState } from "react";
import styles from "./CashToCredit.module.css";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Icon } from "@iconify/react";
import {
  getPlayerCash,
  getUsersByID,
  getUserAvailablePromotion,
  getPlayerBalance,
} from "../../store/slices/user";

export default function CashToCredit() {
  const dispatch = useDispatch();
  const { playerCash, singleUsers, userAvailablePromotion } = useSelector(
    (state) => state.user,
  );
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Promotion detail
  const promotionDetail = singleUsers?.[0]?.PromotionDetail;
  const promotionStatus = singleUsers?.[0]?.PromotionStatus;
  const promotionID = singleUsers?.[0]?.PromotionID;
  const availablePromotions = useMemo(() => {
    if (Array.isArray(userAvailablePromotion)) {
      return userAvailablePromotion;
    }

    if (Array.isArray(userAvailablePromotion?.data)) {
      return userAvailablePromotion.data;
    }

    return [];
  }, [userAvailablePromotion]);
  const storedId = useMemo(() => {
    if (singleUsers?.[0]?.id) {
      return singleUsers[0].id;
    }

    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) return null;

      const parsedUser = JSON.parse(storedUser);
      return (
        parsedUser?.id || parsedUser?.user?.id || parsedUser?.UserID || null
      );
    } catch {
      return null;
    }
  }, [singleUsers]);
  const receivePromo = availablePromotions.find(
    (item) =>
      item?.PromotionID != null &&
      promotionID != null &&
      String(item.PromotionID) === String(promotionID),
  );
  const promotionMinAmount = useMemo(() => {
    const rawValue = receivePromo?.BonusDepMinimum;
    const parsedValue = Number(rawValue);

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }, [receivePromo]);
  const promotionMaxAmount = useMemo(() => {
    const rawValue = receivePromo?.BonusDepMaximum;
    const parsedValue = Number(rawValue);

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }, [receivePromo]);
  const isFixedPromoAmount = Boolean(
    promotionMinAmount &&
    promotionMaxAmount &&
    Math.abs(promotionMinAmount - promotionMaxAmount) < 0.000001,
  );

  useEffect(() => {
    if (isFixedPromoAmount) {
      setAmount(String(promotionMinAmount));
      setMsg((currentMsg) =>
        currentMsg?.type === "success" || currentMsg?.type === "error"
          ? currentMsg
          : null,
      );
      return;
    }

    if (!receivePromo) {
      setAmount("");
      setMsg((currentMsg) =>
        currentMsg?.type === "success" || currentMsg?.type === "error"
          ? currentMsg
          : null,
      );
    }
  }, [isFixedPromoAmount, promotionMinAmount, receivePromo]);

  useEffect(() => {
    if (!storedId) {
      return;
    }

    dispatch(getUsersByID({ id: storedId }));
    dispatch(getUserAvailablePromotion(storedId));
  }, [dispatch, storedId]);

  const handleAmountChange = (event) => {
    if (isFixedPromoAmount) {
      return;
    }

    const nextValue = event.target.value;

    if (nextValue === "") {
      setAmount("");
      setMsg((currentMsg) =>
        currentMsg?.type === "warning" ? null : currentMsg,
      );
      return;
    }

    const numericValue = Number(nextValue);
    if (!Number.isFinite(numericValue)) {
      return;
    }

    if (promotionMaxAmount && numericValue > promotionMaxAmount) {
      setAmount(String(promotionMaxAmount));
      setMsg({
        type: "warning",
        text: `จำนวนเงินต้องไม่เกิน ${promotionMaxAmount.toLocaleString("th-TH")} บาท ตามเงื่อนไขโปรโมชั่น`,
      });
      return;
    }

    setAmount(nextValue);
    setMsg((currentMsg) =>
      currentMsg?.type === "warning" ? null : currentMsg,
    );
  };

  // Handle exchange
  const handleExchange = async (e) => {
    e.preventDefault();
    if (loading) return;

    const cashAmount = isFixedPromoAmount ? promotionMinAmount : Number(amount);
    if (!Number.isFinite(cashAmount) || cashAmount <= 0) {
      setMsg({ type: "warning", text: "กรุณาระบุจำนวนเงินให้ถูกต้อง" });
      return;
    }

    if (promotionMinAmount && cashAmount < promotionMinAmount) {
      setMsg({
        type: "warning",
        text: `จำนวนเงินต้องไม่ต่ำกว่า ${promotionMinAmount.toLocaleString("th-TH")} บาท ตามเงื่อนไขโปรโมชั่น`,
      });
      return;
    }

    if (promotionMaxAmount && cashAmount > promotionMaxAmount) {
      setMsg({
        type: "warning",
        text: `จำนวนเงินต้องไม่เกิน ${promotionMaxAmount.toLocaleString("th-TH")} บาท ตามเงื่อนไขโปรโมชั่น`,
      });
      return;
    }

    setLoading(true);
    setMsg(null);
    try {
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.mil-168.com";
      let url = promotionDetail
        ? `${baseUrl}/userbalance/cashtocreditwithpro/${singleUsers?.[0]?.id}`
        : `${baseUrl}/userbalance/cashtocreditnotpro/${singleUsers?.[0]?.id}`;

      const res = await axios.post(url, { cashAmount });
      if (res?.data?.success) {
        setMsg({
          type: "success",
          text: "แลกเครดิตเรียบร้อย เริ่มสนุกได้เลย!",
        });
        dispatch(getPlayerCash(singleUsers?.[0]?.id));
        dispatch(getUsersByID(singleUsers?.[0]?.id));
        dispatch(getUserAvailablePromotion(singleUsers?.[0]?.id));
        dispatch(getPlayerBalance(singleUsers?.[0]?.id));
      } else {
        setMsg({
          type: "error",
          text: res?.data?.message || "ไม่สามารถทำรายการได้",
        });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "เกิดข้อผิดพลาดในการเชื่อมต่อ" });
    }
    setLoading(false);
    setAmount("");
  };

  const isPromoActive = promotionStatus === "ACTIVE";
  const isDisabled = loading || isPromoActive;
  const isInputDisabled = isDisabled || isFixedPromoAmount;
  const displayAmount = isFixedPromoAmount
    ? String(promotionMinAmount ?? "")
    : amount;
  const amountPlaceholder = isFixedPromoAmount
    ? `${promotionMinAmount.toLocaleString("th-TH")}`
    : promotionMinAmount && promotionMaxAmount
      ? `ระบุจำนวนเงิน ${promotionMinAmount.toLocaleString("th-TH")} - ${promotionMaxAmount.toLocaleString("th-TH")}`
      : promotionMinAmount
        ? `ระบุจำนวนเงินไม่ต่ำกว่า ${promotionMinAmount.toLocaleString("th-TH")}`
        : promotionMaxAmount
          ? `ระบุจำนวนเงินไม่เกิน ${promotionMaxAmount.toLocaleString("th-TH")}`
          : "ระบุจำนวนเงิน";

  return (
    <div className={styles.wrapper}>
      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroEmoji}>🔄</div>
        <h2 className={styles.heroTitle}>แลกกระเป๋าเงินสด</h2>
        <p className={styles.heroDesc}>
          เปลี่ยนเงินสดในกระเป๋าเป็นเครดิตสำหรับเล่นเกม
        </p>
      </div>

      {/* ── Balance Card ── */}
      <div className={styles.balanceCard}>
        <div>
          <div className={styles.balanceLabel}>เงินสดคงเหลือ</div>
          <div className={styles.balanceValue}>
            {playerCash ? parseFloat(playerCash).toLocaleString() : "0"}
            <span
              style={{
                fontSize: "0.6em",
                marginLeft: "6px",
                color: "var(--secondaryColor)",
              }}
            >
              THB
            </span>
          </div>
        </div>
        <div className={styles.balanceIcon}>
          <Icon icon="solar:wallet-money-bold-duotone" width={28} height={28} />
        </div>
        <div className={styles.balanceGlow} />
      </div>

      {/* ── Promo Status ── */}
      {promotionDetail && (
        <div className={styles.promoCard}>
          <div className={styles.promoHeader}>
            <div className={styles.promoIcon}>
              <Icon icon="solar:gift-bold-duotone" width={22} height={22} />
            </div>
            <div className={styles.promoContent}>
              <div className={styles.promoLabel}>โปรโมชั่นที่เลือกไว้</div>
              <div className={styles.promoDetail}>{promotionDetail}</div>
              {isPromoActive && (
                <div
                  className={styles.statusMsg + " " + styles.warningText}
                  style={{
                    marginTop: "8px",
                    padding: 0,
                    background: "none",
                    border: "none",
                  }}
                >
                  <Icon icon="solar:info-circle-bold" width={16} />
                  <span>กำลังใช้งานโปรโมชั่น</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Action Form ── */}
      <div className={styles.formCard}>
        <form onSubmit={handleExchange}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>จำนวนเงินที่ต้องการแลก</label>
            <input
              type="number"
              min={promotionMinAmount || 1}
              max={promotionMaxAmount || undefined}
              className={styles.amountInput}
              placeholder={amountPlaceholder}
              value={displayAmount}
              onChange={handleAmountChange}
              disabled={isInputDisabled}
              readOnly={isFixedPromoAmount}
              required
            />
          </div>

          {receivePromo && (
            <div className={styles.recommendBox}>
              <Icon
                icon="solar:lightbulb-bolt-bold"
                className={styles.recommendIcon}
                width={20}
              />
              <span className={styles.recommendText}>
                {isFixedPromoAmount ? (
                  <>
                    โปรโมชั่นนี้กำหนดยอดแลกเป็น{" "}
                    <span className={styles.recommendHighlight}>
                      {promotionMinAmount.toLocaleString("th-TH")} บาท
                    </span>{" "}
                    ระบบจึงล็อกจำนวนเงินให้อัตโนมัติ
                  </>
                ) : promotionMinAmount && promotionMaxAmount ? (
                  <>
                    โปรโมชั่นนี้กำหนดยอดแลกได้ตั้งแต่{" "}
                    <span className={styles.recommendHighlight}>
                      {promotionMinAmount.toLocaleString("th-TH")} บาท
                    </span>{" "}
                    ถึง{" "}
                    <span className={styles.recommendHighlight}>
                      {promotionMaxAmount.toLocaleString("th-TH")} บาท
                    </span>
                  </>
                ) : promotionMinAmount ? (
                  <>
                    โปรโมชั่นนี้กำหนดยอดแลกไม่ต่ำกว่า{" "}
                    <span className={styles.recommendHighlight}>
                      {promotionMinAmount.toLocaleString("th-TH")} บาท
                    </span>
                  </>
                ) : (
                  <>
                    โปรโมชั่นนี้กำหนดยอดแลกได้ไม่เกิน{" "}
                    <span className={styles.recommendHighlight}>
                      {promotionMaxAmount?.toLocaleString("th-TH")} บาท
                    </span>
                  </>
                )}
              </span>
            </div>
          )}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isDisabled && !loading}
          >
            {loading ? (
              <Icon
                icon="solar:spinner-bold-duotone"
                width={24}
                style={{ animation: "spin 1s linear infinite" }}
              />
            ) : (
              <Icon icon="solar:card-transfer-bold-duotone" width={24} />
            )}
            {loading ? "กำลังทำรายการ..." : "ยืนยันแลกเครดิต"}
          </button>
        </form>

        {/* ── Status Messages ── */}
        {isPromoActive && (
          <div className={styles.warningBox}>
            <Icon
              icon="solar:danger-triangle-bold"
              className={styles.warningIcon}
              width={20}
            />
            <span className={styles.warningText}>
              ท่านอยู่ระหว่างรับโปรโมชั่นและยังทำเทิร์นไม่ครบตามกำหนด
              กรุณาเคลียร์เทิร์นโอเวอร์ก่อนทำการแลกเครดิตเพิ่ม
            </span>
          </div>
        )}

        {msg && (
          <div
            className={`${styles.statusMsg} ${
              msg.type === "success"
                ? styles.msgSuccess
                : msg.type === "warning"
                  ? styles.msgWarning
                  : styles.msgError
            }`}
          >
            <Icon
              icon={
                msg.type === "success"
                  ? "solar:check-circle-bold"
                  : msg.type === "warning"
                    ? "solar:danger-triangle-bold"
                    : "solar:close-circle-bold"
              }
              width={20}
            />
            <span>{msg.text}</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
